import { FilesetResolver, HandLandmarker, HandLandmarkerResult } from '@mediapipe/tasks-vision'

interface HandPosition {
  x: number
  y: number
  z: number
  size: number  // Hand size (0-1 normalized, larger = closer to camera)
}

export class HandTracker {
  private handLandmarker: HandLandmarker | null = null
  private video: HTMLVideoElement | null = null
  private lastHands: HandPosition[] = []
  private isProcessing = false

  async initialize(): Promise<void> {
    // Get video element
    this.video = document.getElementById('webcam') as HTMLVideoElement
    if (!this.video) {
      throw new Error('Video element not found')
    }

    // Request camera access
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'user',
        width: { ideal: 640 },
        height: { ideal: 480 }
      }
    })

    this.video.srcObject = stream
    await this.video.play()

    // Initialize MediaPipe HandLandmarker
    const vision = await FilesetResolver.forVisionTasks(
      'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
    )

    this.handLandmarker = await HandLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task',
        delegate: 'GPU'
      },
      runningMode: 'VIDEO',
      numHands: 5,
      minHandDetectionConfidence: 0.5,
      minHandPresenceConfidence: 0.5,
      minTrackingConfidence: 0.5
    })

    // Start detection loop
    this.detectHands()
  }

  private detectHands = (): void => {
    if (!this.handLandmarker || !this.video || this.isProcessing) {
      requestAnimationFrame(this.detectHands)
      return
    }

    this.isProcessing = true

    const now = performance.now()
    const results: HandLandmarkerResult = this.handLandmarker.detectForVideo(this.video, now)

    // Extract palm centers (landmark 9 is middle finger MCP, good approximation for palm center)
    // Alternative: average of landmarks 0, 5, 9, 13, 17 for true palm center
    this.lastHands = results.landmarks.map(landmarks => {
      // Use landmark 9 (MIDDLE_FINGER_MCP) as palm center approximation
      const palmCenter = landmarks[9]
      if (!palmCenter) {
        return { x: 0.5, y: 0.5, z: 0, size: 0.2 }
      }

      // Calculate hand size from wrist to middle finger tip distance
      const wrist = landmarks[0]
      const middleTip = landmarks[12]
      let handSize = 0.2  // Default size
      if (wrist && middleTip) {
        handSize = Math.sqrt(
          Math.pow(middleTip.x - wrist.x, 2) +
          Math.pow(middleTip.y - wrist.y, 2)
        )
      }

      return {
        x: palmCenter.x,
        y: palmCenter.y,
        z: handSize * 2,  // Depth based on size
        size: handSize    // Raw hand size (typically 0.15 - 0.4)
      }
    })

    this.isProcessing = false
    requestAnimationFrame(this.detectHands)
  }

  public getHands(): HandPosition[] {
    return this.lastHands
  }

  public dispose(): void {
    if (this.video?.srcObject) {
      const stream = this.video.srcObject as MediaStream
      stream.getTracks().forEach(track => track.stop())
    }
    this.handLandmarker?.close()
  }
}
